import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { IconButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { accountListSelector, useSuspendedAccounts } from '../../../fetchHooks/useAccount';
import { useSuspendedFavoriteAccount } from '../../../fetchHooks/useFavoriteAccount';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation from '../../../hooks/useTranslation';
import { useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import useFocusContext from '../../NettbankCreateTransactions/hooks/useFocusContext';
import FocusMonitor from '../../shared/FocusMonitor/FocusMonitor';
import PaymentAndTransferForm, {
    paymentAndTransferFormSwitchId,
} from '../PaymentAndTransferForm/PaymentAndTransferForm';
import MultiformWrapper from './MultiformWrapper';

const FocusMultiformModal: React.FC = () => {
    const { data: accounts } = useSuspendedAccounts({ select: accountListSelector });
    const { data: favoriteAccountNumber } = useSuspendedFavoriteAccount();

    const favoriteAccount = useMemo(
        () => accounts.find((account) => account.accountNumber.value === favoriteAccountNumber?.value),
        [favoriteAccountNumber, accounts],
    );
    const translated = useTranslation();
    const { active, toggleSwitchState, stopTimeoutOnSuccess, resetForm } = useFocusContext();
    const { showSigningFlow, signingOperationPending, signingStatus, dispatch, handleCancelSigningFlow } =
        useSigningContext();
    const showPaymentResult = useShowPaymentResult();

    const closeAndReset = useCallback(() => {
        handleCancelSigningFlow();
        dispatch({ type: Actions.RESET });
        resetForm.set(true);
        setTimeout(() => {
            active.set(false);
            resetForm.set(false);
        }, 5);
    }, [resetForm, active, dispatch, handleCancelSigningFlow]);

    return (
        <section
            role="dialog"
            className="nettbank-transactions"
            aria-modal
            aria-labelledby={paymentAndTransferFormSwitchId}
        >
            {!active.value && (
                <button type="button" onClick={() => active.set(true)} className="sgw-skip-link">
                    {translated.openPaymentWindow}
                </button>
            )}
            <FocusMonitor
                className={classNames('nettbank-transactions__wrapper focus-monitor', {
                    active: active.value,
                    'hide-cancel-cross':
                        toggleSwitchState.value === 'hidden' || (showSigningFlow && !showPaymentResult),
                })}
                active={active.value}
                onEnter={() => active.set(true)}
                onExit={closeAndReset}
                disableExit={signingOperationPending}
                timeout={!stopTimeoutOnSuccess.value && signingStatus === 'COMPLETED' ? 2000 : undefined}
            >
                <section
                    className={classNames('nettbank-transactions__container is-themed', {
                        active: active.value,
                    })}
                    data-testid="payment-and-transfer-wrapper"
                >
                    {active.value && (
                        <IconButton
                            className="nettbank-transaction__close-button"
                            onClick={closeAndReset}
                            icon={IconTypes.KRYSS_24}
                            screenReaderText={translated.closeWindow}
                        />
                    )}
                    <PaymentAndTransferForm
                        account={favoriteAccount}
                        withFocusContext={false}
                        transferForm={
                            <MultiformWrapper
                                transactionType="transfer"
                                account={favoriteAccount}
                                onClose={closeAndReset}
                                type="focus"
                            />
                        }
                        paymentForm={
                            <MultiformWrapper
                                transactionType="payment"
                                onClose={closeAndReset}
                                account={favoriteAccount}
                                type="focus"
                            />
                        }
                    />
                </section>
            </FocusMonitor>
        </section>
    );
};

export default FocusMultiformModal;
